import { useCallback } from "react";
import { useDebateStore } from "../store/debateStore";
import { useDiscussionStream } from "./useDiscussionStream";
import {
  mintUserGestureToken,
  type GestureOrigin,
  type UserGestureToken,
} from "../domain/types";
import type { DebateStartOptions } from "../store/slices/sessionSlice";

export interface GestureSeed {
  origin: GestureOrigin;
  isTrusted: boolean;
  timeStamp: number;
}

export interface StartDebateOptions extends DebateStartOptions {
  /** When true (default), use the wizard's commit snapshot. */
  useWizardCommit?: boolean;
}

export interface UseStartDebateApi {
  /** Mint a gesture token from the supplied event seed and start the debate. */
  start(seed: GestureSeed, opts?: StartDebateOptions): Promise<void>;
  /** Cancel any in-flight stream. */
  cancel(reason?: string): void;
}

/**
 * Hook for starting / cancelling a debate while strictly preserving the
 * `mintUserGestureToken` autoplay-policy contract (architecture amendment #1).
 * Only callers that own a *real* DOM event (isTrusted=true) may successfully
 * mint a token — synthetic invocations throw `AudioGestureRequiredError`.
 */
export function useStartDebate(): UseStartDebateApi {
  const stream = useDiscussionStream();

  const start = useCallback(
    async (seed: GestureSeed, opts?: StartDebateOptions): Promise<void> => {
      const gesture: UserGestureToken = mintUserGestureToken(seed);
      const store = useDebateStore.getState();
      // Default: pull config from the wizard slice (preferred path).
      const useWizard = opts?.useWizardCommit !== false;
      let runOpts: DebateStartOptions | undefined;
      let query: string;
      let topology = store.topology;
      if (useWizard) {
        const commit = store.commitToDebate();
        query = commit.query || store.query;
        topology = commit.topology;
        runOpts = {
          personas: commit.personas,
          maxRounds: commit.maxRounds,
          autoFinalize: commit.autoFinalize,
          critiqueStrictness: commit.critiqueStrictness,
          // Konsey Cognitive Renaissance — Bölüm A0/A1.
          terminationProfile: commit.terminationProfile,
          // Görev 3-5. `DecisionConstraints` fields are all optional
          // strings with no index signature; the wire contract only
          // needs a plain string-keyed dict, and `setConstraints`
          // already strips empty/undefined entries before this point.
          privateMode: commit.privateMode,
          urgency: commit.urgency,
          constraints: commit.constraints as Record<string, string>,
          // Persona Kütüphanesi — kalıcı takım / tekil kimlik seçimi.
          libraryTeamId: commit.libraryTeamId,
          libraryUnitId: commit.libraryUnitId,
          libraryPersonaIds: commit.libraryPersonaIds,
          userDebatePrompt: commit.userDebatePrompt,
          // Clarification resubmission — the CALLER's opts (e.g. from
          // `ClarificationModal`) take precedence over the wizard
          // commit snapshot, which never carries these by definition.
          ...(opts?.clarificationAnswers
            ? { clarificationAnswers: opts.clarificationAnswers }
            : {}),
          ...(opts?.skipClarification ? { skipClarification: true } : {}),
        };
      } else {
        query = store.query;
        runOpts = opts;
      }
      await stream.start(query, topology, gesture, runOpts);
    },
    [stream],
  );

  const cancel = useCallback(
    (reason?: string): void => {
      stream.cancel(reason ?? "user-cancelled");
    },
    [stream],
  );

  return { start, cancel };
}
